// @ts-strict-ignore
import { Linking } from 'react-native'
import { call, put, select, take, race, delay, spawn } from 'typed-redux-saga'

import Toktok from 'APP/Services/Toktok'
import { VIDEO_CALL_END_ACTIONS, videoSessionActions } from './slice'
import * as StoreReview from 'expo-store-review'

import Alert from 'APP/Converse/Alert'

import Config from 'APP/Config'
import { ANDROID_API_VERSION } from 'APP/Config/constants'
import I18n from 'APP/I18n'
import { navigationRef as Nav } from 'APP/Nav'
import * as NestedNavHelper from 'APP/Nav/NestedNavHelper'
import { logger, trackDdEvent } from 'APP/Lib/Datadog'

// Api
import {
  checkCameraState,
  requestCameraPermission,
  checkMicrophoneState,
  requestMicrophonePermission,
  checkBluetoothConnectState,
  requestBluetoothConnectPermission,
} from 'APP/NativeModules/PermissionsService'
import { configureOpenTokAudioForVideoCall } from 'APP/NativeModules/OpenTokAudioManager'
import Analytics from 'APP/Services/Analytics'

import { selectPractitionerId, selectVideoKeepAliveEpisodeId } from './selectors'
import type { RootState } from 'APP/Store/CreateStore'
import { isAndroid, isIOS, isMobile, meetsMinimumVersion } from 'APP/Helpers/checkPlatform'
import { nativeCallAndroidActions } from 'APP/Store/NativeCallAndroid'
import { canUseNativeCall } from 'APP/Store/NativeCallIOS/sagas'
import { brownfieldActions, selectSkipTokenExchange } from 'APP/Store/Brownfield'
import { isEmbeddedEnvironment } from 'APP/BrownfieldBridge/utils'
import * as BrownfieldBridgeInternal from 'APP/BrownfieldBridge/BrownfieldBridgeInternal'

import { isHeadlessTask } from 'APP/Lib/HeadlessTask'
import type { PayloadAction } from '@reduxjs/toolkit'
import type {
  ConnectToOpentokPayload,
  PatientAcceptedCallPayload,
  PatientDeclinedCallPayload,
  TrackVideoQualityStatsPayload,
} from 'APP/Store/VideoSession/types'
import { LoginTypes, selectAccessToken, selectLoginState } from 'APP/Redux/LoginRedux'
import { waitForNavigationReady } from 'APP/Sagas/StartupSagas'
import { nativeCallIOSActions } from 'APP/Store/NativeCallIOS'
import { waitForValidAccessTokenWithTimeout } from '../Login/sagas'
import type { TypedRoute } from 'APP/Nav/types'

export const selectProfile = (state: RootState) => state.patient && state.patient.profile
export const selectPractitionersList = (state: RootState) => state.history.practitioners || {}
export const selectLastCallAcceptedAt = (state: RootState) => state.videoSession?.lastCallAcceptedAt
export const selectVideoSession = (state: RootState) => state.videoSession
export const selectLastVideoCall = (state: RootState) => state.videoSession?.lastVideoCall
export const selectVideoSessionId = (state: RootState) => state.videoSession?.sessionId

export const Types = {
  CONNECT_TO_OPENTOK: 'CONNECT_TO_OPENTOK',
}

export const Actions = {
  connectToOpentok: (payload: ConnectToOpentokPayload) => ({
    type: Types.CONNECT_TO_OPENTOK,
    payload: payload,
  }),
}

const VIDEO_CALL_ENDED_TYPES = [
  'videoSession/patientEndedCall',
  'videoSession/platformEndedCall',
  'videoSession/platformStreamDestroyed',
]

export function* waitForAppHydrated() {
  const { hydrated } = yield select(selectLoginState)
  if (!hydrated) {
    yield take(LoginTypes.REHYDRATE_REDUCER)
  }
}

export function* waitForSessionConnected() {
  let videoSession = yield* select(selectVideoSession)

  if (videoSession.connected) {
    return
  }

  const startTime = Date.now()

  logger.info('Video session not already connected, waiting for connection before continuing', {
    videoSessionId: videoSession.sessionId,
  })

  while (!videoSession.connected) {
    yield delay(100)
    videoSession = yield* select(selectVideoSession)
  }

  const endTime = Date.now()

  logger.info('Video session connected, continuing', {
    videoSessionId: videoSession.sessionId,
    timeElapsed: endTime - startTime,
  })
}

// TODO: lower default timeoutInMs after capturing data from timeElapsed logs
export function* waitForSessionConnectedWithTimeout(timeoutInMs: number) {
  const { timedOutWaitingForSessionConnected } = yield* race({
    sessionConnected: call(waitForSessionConnected),
    timedOutWaitingForSessionConnected: delay(timeoutInMs),
  })
  // Return true if successfully connected, return false if timed out
  const sessionConnected = !timedOutWaitingForSessionConnected

  if (!sessionConnected) {
    logger.warn('Timed out while waiting for OpenTok session to connect.', {
      timeoutInMs,
    })
    return sessionConnected
  }

  return sessionConnected
}

export function* getCaller(id: string | undefined | null = undefined) {
  const practitionerId = id ? id : yield* select(selectPractitionerId)
  if (!practitionerId) return {}
  const practitioners = Object.values(yield select(selectPractitionersList))
  return practitioners.find(
    (practitioner) => String(practitioner.app_id) === String(practitionerId)
  )
}

function showAppSettingsAlert() {
  const cta = isMobile()
    ? [
        {
          text: I18n.t('IncomingCallScreen.permissionCTA'),
          onPress: () => Linking.openSettings(),
        },
      ]
    : [
        {
          text: I18n.t('IncomingCallScreen.bluetoothConnectDisclaimerCta'),
        },
      ]

  Alert.alert('', I18n.t('IncomingCallScreen.permissionToConnect'), cta)
}

export function* disconnect() {
  logger.info('VideoSession disconnect saga started')
  yield put(videoSessionActions.disconnectFromSession())
}

export function* trackVideoQualityStats(action: PayloadAction<TrackVideoQualityStatsPayload>) {
  const { current, previous } = action.payload
  const eventProps = { value: current, previous_value: previous }
  yield call(Analytics.trackEvent, 'video_quality_change', eventProps)
}

export function* connectToOpenTok(action: PayloadAction<ConnectToOpentokPayload>) {
  const patientProfile = yield* select(selectProfile)
  let accessToken = yield* select(selectAccessToken)
  const { connected, connecting } = yield* select(selectVideoSession)
  const { onDemandSessionId } = action.payload

  logger.info('connectToOpenTok called with on-demand session ID', {
    onDemandSessionId,
  })

  // Skip if not signed in
  // Skip if not provided in config
  // Skip if already connected or connecting
  if (
    !patientProfile?.id ||
    !accessToken ||
    !Config.TOKTOK_SERVICE_URL ||
    connected ||
    connecting
  ) {
    return
  }

  accessToken = yield* waitForValidAccessTokenWithTimeout()

  if (!accessToken) {
    logger.info('Token is not valid, will rerun connectToOpenTok in 5 seconds')
    yield put(Actions.connectToOpentok({ onDemandSessionId }))
    return
  }

  try {
    const toktok = Toktok.create(accessToken)
    const sessionCredentials = yield* call(toktok.getSessionCredentials, onDemandSessionId)
    const credentials = {
      apiKey: sessionCredentials.data.api_key,
      sessionId: sessionCredentials.data.session_id,
      token: sessionCredentials.data.token,
    }

    logger.info('Connecting to Vonage', { sessionId: credentials.sessionId })

    yield put(videoSessionActions.connectToSession(credentials))

    logger.info('Successfully connected to Vonage', { sessionId: credentials.sessionId })
  } catch (e) {
    logger.error('Error connecting to Vonage, will retry in 5 seconds', {}, e)
    yield put(Actions.connectToOpentok({ onDemandSessionId }))
  }
}

export const isOnVideoCall = () =>
  !isHeadlessTask() && ['videoCall'].indexOf(Nav.getCurrentRoute()?.name) !== -1

export const isVideoCallRoute = () =>
  !isHeadlessTask() && ['incomingCall', 'videoCall'].indexOf(Nav.getCurrentRoute()?.name) !== -1

interface OnDemandCallRequestedArgs {
  payload: {
    callerId?: number
    episodeId?: string
    useFullScreen?: boolean
    sessionId: string
  }
}

/**
 * Saga that initiates the on-demand video call flow.
 * - If the user is already on a video call route, this is no-op.
 * - Otherwise:
 *    + Call details are persisted to Redux, so they can be leveraged for call end display/navigation.
 *    + The user is navigated to the incoming call screen.
 */
export function* onDemandCallRequested(args: OnDemandCallRequestedArgs) {
  const { callerId, episodeId, useFullScreen = false, sessionId } = args.payload
  const isCurrentRouteVideoCall = isVideoCallRoute()

  logger.info('On-demand video call requested', {
    isCurrentRouteVideoCall,
    onDemandSessionId: sessionId,
    useFullScreen,
  })

  // Nothing to do if the user is already on a video call route
  if (isCurrentRouteVideoCall) {
    return
  }

  // Calls delivered over SSE must be tracked too, otherwise `callEnded` can't dismiss the
  // incoming-call notification and the member can accept a call that already ended.
  if (isAndroid()) {
    yield put(nativeCallAndroidActions.incomingCallRequested({ sessionId }))
  }

  if (callerId !== undefined && episodeId) {
    // Persist call details, which is used to manage navigation on call end
    yield put(
      videoSessionActions.saveLastVideoCallInfo({ callerId: callerId.toString(), episodeId })
    )
  }

  // Navigate to the incoming call UI
  const callerProfile = yield* call(getCaller, callerId?.toString())

  if (Nav.getCurrentRoute()?.name === 'videoCallEnded') {
    // Close the current videoCallEnded screen before navigating to new incomingCall
    yield call(NestedNavHelper.pop)
  }

  yield call(Nav.navigate, 'incomingCall', {
    caller: callerProfile,
    videoSessionId: sessionId,
    isFullScreen: useFullScreen,
  })
}

export function* sessionDisconnected(action: PayloadAction) {
  const currentRoute = Nav.isReady() ? (Nav.getCurrentRoute() as TypedRoute | undefined) : undefined

  // When the feature flag is enabled: only execute end-of-call actions if the user was on a video call.
  // Outside of a call, there is no need to run this & if we run it, we run into the long-idling
  // issue on iOS.
  // When the feature flag is disabled, always run the saga.
  const shouldRunVideoCallDismissal = currentRoute?.name === 'videoCall'

  logger.info('sessionDisconnected saga started', {
    currentRoute,
    shouldRunVideoCallDismissal,
  })

  if (shouldRunVideoCallDismissal) {
    yield* call<Parameters<typeof dismissVideoCallScreen>, typeof dismissVideoCallScreen>(
      dismissVideoCallScreen,
      { type: action.type, payload: { hasDisconnected: true } }
    )
  }
}

export function* shouldPromptForAppRating() {
  const deviceSupportsInAppReview = yield* call(StoreReview.isAvailableAsync)
  const lastCallAcceptedAt = yield* select(selectLastCallAcceptedAt)
  const callDuration = lastCallAcceptedAt ? Date.now() - lastCallAcceptedAt : 0
  const durationThresholdMet = callDuration >= Config.APP_REVIEW_MIN_CALL_DURATION
  const isVideoCallRouteCheck = yield* call(isVideoCallRoute)
  const isMobileCheck = yield* call(isMobile)
  const shouldPrompt =
    !isEmbeddedEnvironment() &&
    !isVideoCallRouteCheck &&
    isMobileCheck &&
    deviceSupportsInAppReview &&
    durationThresholdMet

  logger.info('shouldPromptForAppRating check', {
    callDuration,
    deviceSupportsInAppReview,
    durationThresholdMet,
    lastCallAcceptedAt,
    isEmbeddedEnvironment: isEmbeddedEnvironment(),
    isMobile: isMobileCheck,
    isVideoCallRoute: isVideoCallRouteCheck,
    shouldPrompt,
  })

  return shouldPrompt
}

export function* afterCallAppRatingPromptCheck(action: PayloadAction<any>) {
  logger.info('afterCallAppRatingPromptCheck saga started', { actionType: action.type })

  try {
    const shouldPrompt = yield* call(shouldPromptForAppRating)
    if (shouldPrompt) {
      logger.info('Attempting to prompt user for app review after delay', {
        promptDelay: Config.APP_REVIEW_PROMPT_DELAY,
      })
      yield delay(Config.APP_REVIEW_PROMPT_DELAY)
      yield call(StoreReview.requestReview)
    }
  } catch (e) {
    const { code } = e as { code: string }
    if (code === 'ERR_R_M_UNSUCCESSFUL_TASK') {
      logger.info('Unable to complete in-app review (play services likely not installed)')
    } else if (code === 'ERR_MISSING_CURRENT_WINDOW_SCENE') {
      logger.info('Unable to complete in-app review (no active window scene)')
    } else {
      logger.warn('Error triggering in-app review', {}, e)
    }
  }
}

interface DismissVideoCallScreenArgs {
  payload?: {
    declined?: boolean
    hasDisconnected?: boolean
    onDemandSessionId?: string
  }
  type?: string
}

// Notifies the native host that an embedded call ended.
// Returns true when control was returned to the host (caller should stop).
export function* handOffEmbeddedCallEnded(skipTokenExchange: boolean) {
  const episodeId = (yield* select(selectVideoKeepAliveEpisodeId)) ?? undefined
  BrownfieldBridgeInternal.triggerEvent({
    type: 'VIDEO_CALL_ENDED',
    payload: episodeId ? { episodeId } : {},
  })

  if (!skipTokenExchange) {
    return false
  }

  yield put(brownfieldActions.clearSkipTokenExchange())
  // Returning to the host does not tear down the bridge-owned OpenTok session.
  yield put(videoSessionActions.disconnectFromSession())
  BrownfieldBridgeInternal.popToNative(true)
  return true
}

export function* dismissVideoCallScreen(args?: DismissVideoCallScreenArgs) {
  const hasDisconnected = args?.payload?.hasDisconnected
  const onDemandSessionId = args?.payload?.onDemandSessionId
  const currentRoute = Nav.isReady() ? (Nav.getCurrentRoute() as TypedRoute | undefined) : undefined

  logger.info('Running dismissVideoCallScreen saga...', {
    actionType: args?.type,
    currentRoute,
    hasDisconnected,
    onDemandSessionId,
  })

  const isEmbedded = isEmbeddedEnvironment()
  const skipTokenExchange = isEmbedded ? yield* select(selectSkipTokenExchange) : false

  // Delegate platform-specific effects to native sagas
  // These sagas are responsible for making appropriate checks based on their own slice state,
  // to determine if any effects should be run.
  if (isAndroid()) {
    yield put(nativeCallAndroidActions.callEnded({ sessionId: onDemandSessionId }))
  }

  if (isIOS()) {
    yield put(nativeCallIOSActions.endNativeCall({ onDemandSessionId }))
  }

  const isEndCallAction = Boolean(args?.type && VIDEO_CALL_ENDED_TYPES.includes(args.type))
  const isOnVideoRoute = currentRoute?.name === 'incomingCall' || currentRoute?.name === 'videoCall'
  const currentVideoCallSessionId = isOnVideoRoute ? currentRoute.params.videoSessionId : null

  if (
    onDemandSessionId &&
    currentVideoCallSessionId &&
    onDemandSessionId !== currentVideoCallSessionId
  ) {
    logger.info('onDemandSessionId does not match session ID for current route, exiting early', {
      currentRoute,
      onDemandSessionId,
    })
    return
  }

  // Host lifecycle effects must not depend on React Navigation being ready. During an embedded
  // VoIP cold start, the call can end before a route exists, but the native host still needs the
  // end event and control returned to it.
  if (isEmbedded && isEndCallAction) {
    const returnedToHost = yield* handOffEmbeddedCallEnded(skipTokenExchange)
    if (returnedToHost) {
      return
    }
  }

  if (!isOnVideoRoute) {
    logger.info('Not on video call route, exiting early')
    return
  }

  if (onDemandSessionId && !currentVideoCallSessionId) {
    logger.warn('No video session ID found in route params, exiting early', {
      currentRoute,
      onDemandSessionId,
    })
    return
  }

  if (Nav.getRootState()?.routes?.length <= 1) {
    yield call(Nav.reset, { index: 0, routes: [{ name: 'tabbar' }] })
  } else {
    const memberDeclinedCall =
      args?.type === 'videoSession/patientEndedCall' && args?.payload?.declined

    // TODO: Make handling of incomingCallScreen explicitly separate from handling
    // of videoCallScreen. VIDEO_CALL_ENDED_TYPES does this implicitly by only catching
    // actions that are emitted solely while on a video call the else check here
    // does not work well when on a videoCallScreen, because pop() could take user
    // back to incomingCallScreen
    if (isEndCallAction) {
      const lastVideoCall = yield* select(selectLastVideoCall)
      const { practitionerId, episodeId } = lastVideoCall
      const caller = yield* call(getCaller, practitionerId)

      if (memberDeclinedCall) {
        yield call(trackDdEvent, 'video-call:declined')

        // We want to navigate the member to the episode tied to the video call
        // However if they were already on it, we can just pop them back from the incoming call screen
        const routes = NestedNavHelper.navigation.getState().routes
        const lastRoute = routes.length > 1 ? routes[routes.length - 2] : undefined
        const lastRouteWasRelatedConversation =
          (lastRoute?.name === 'conversation' || lastRoute?.name === 'newConversation') &&
          (lastRoute?.params as { channelId?: unknown })?.channelId === episodeId

        if (lastRouteWasRelatedConversation) {
          yield call(NestedNavHelper.pop)
        } else if (episodeId) {
          yield call(NestedNavHelper.replace, 'conversation', { channelId: episodeId })
        } else {
          yield call(Nav.reset, { index: 0, routes: [{ name: 'tabbar' }] })
        }
      } else {
        yield call(trackDdEvent, 'video-call:end')
        yield call(NestedNavHelper.replace, 'videoCallEnded', { caller, episodeId })
      }
    } else {
      yield call(NestedNavHelper.pop)
    }
  }

  // Disconnect from the session & reset state.
  // This must be done last because we are using the video call metadata above for navigation.
  if (!hasDisconnected) {
    yield call(disconnect)
  }

  yield put(videoSessionActions.videoCallDismissed())
}

export function showBluetoothAlert() {
  return new Promise((resolve) => {
    Alert.alert(
      '',
      I18n.t('IncomingCallScreen.bluetoothConnectDisclaimer'),
      [
        {
          text: I18n.t('IncomingCallScreen.bluetoothConnectDisclaimerCta'),
          onPress: () => resolve('ok'),
        },
      ],
      {
        cancelable: false,
      }
    )
  })
}

export function* acceptCallRequest(onDemandSessionId: string) {
  logger.info('acceptCallRequest saga started', {
    onDemandSessionId,
  })

  // Get valid access token
  const validTokenTimeout = 5000
  const accessToken = yield* waitForValidAccessTokenWithTimeout(validTokenTimeout)

  if (!accessToken) {
    logger.warn(
      'Unable to retrieve a valid access token before timing out, so cannot accept video call in Toktok',
      { validTokenTimeout, onDemandSessionId }
    )
    return
  }

  try {
    logger.info('Accepting call request from Toktok by session ID', { onDemandSessionId })
    const toktok = Toktok.create(accessToken)
    yield* call(toktok.acceptCallRequest, onDemandSessionId)
    logger.info('Successfully accepted call request from Toktok by session ID', {
      onDemandSessionId,
    })
  } catch (e) {
    logger.error('Error accepting call request from Toktok by session ID', { onDemandSessionId }, e)
  }
}

export function* declineCallRequest(onDemandSessionId: string) {
  logger.info('declineCallRequest saga started', {
    onDemandSessionId,
  })

  // Get valid access token
  const validTokenTimeout = 5000
  const accessToken = yield* waitForValidAccessTokenWithTimeout(validTokenTimeout)

  if (!accessToken) {
    logger.warn(
      'Unable to retrieve a valid access token before timing out, so cannot decline video call in Toktok',
      { validTokenTimeout, onDemandSessionId }
    )
    return
  }

  try {
    logger.info('Declining call request from Toktok by session ID', { onDemandSessionId })
    const toktok = Toktok.create(accessToken)
    yield* call(toktok.declineCallRequest, onDemandSessionId)
    logger.info('Successfully declined call request from Toktok by session ID', {
      onDemandSessionId,
    })
  } catch (e) {
    logger.error('Error declining call request from Toktok by session ID', { onDemandSessionId }, e)
  }
}

export function* patientAcceptedCall(action: PayloadAction<PatientAcceptedCallPayload>) {
  const isOnVideoCallCheck = isOnVideoCall()

  logger.info('patientAcceptedCall saga started', {
    payload: action.payload,
    isOnVideoCall: isOnVideoCallCheck,
  })

  if (isOnVideoCallCheck) {
    logger.info('patientAcceptedCall saga running while currently on video call. Early exiting.')
    return
  }

  const { onDemandSessionId } = action.payload

  logger.info('Patient accepted call with session ID.', {
    onDemandSessionId,
  })
  yield spawn(acceptCallRequest, onDemandSessionId)

  yield put(Actions.connectToOpentok({ onDemandSessionId }))

  const sessionConnected = yield* call(waitForSessionConnectedWithTimeout, 12_000)

  if (!sessionConnected) {
    logger.warn(
      'patientAcceptedCall saga timed out while waiting for OpenTok session to connect. Cannot accept call. Early exiting and dismissing video call screen.'
    )
    yield call(dismissVideoCallScreen, { type: action.type })
    return
  }

  yield call(trackDdEvent, 'video-call:accepted')

  // Check audio/video permissions first
  let cameraPermission = yield* call(checkCameraState)
  let microphonePermission = yield* call(checkMicrophoneState)

  // Android 12 introduces Bluetooth permissions
  const shouldAskForBluetooth = isAndroid() && meetsMinimumVersion(ANDROID_API_VERSION.ANDROID_12)
  let bluetoothConnectPermission = yield* call(checkBluetoothConnectState)

  if (cameraPermission !== 'granted') {
    yield put(videoSessionActions.patientAskingVideoPermission())

    cameraPermission = yield* call(requestCameraPermission)
    if (cameraPermission === 'granted') {
      yield put(videoSessionActions.patientAcceptedVideoPermission())
    } else {
      yield put(videoSessionActions.patientDeclinedVideoPermission())
      logger.info('Patient declined video permission, dismissing video call screen', {
        cameraPermission,
      })
      yield call(dismissVideoCallScreen, { type: action.type })
      showAppSettingsAlert()
      return
    }
  }

  if (microphonePermission !== 'granted') {
    yield put(videoSessionActions.patientAskingAudioPermission())

    microphonePermission = yield* call(requestMicrophonePermission)

    if (microphonePermission === 'granted' || microphonePermission === 'unavailable') {
      // Microphone permissions are never asked on iOS
      yield put(videoSessionActions.patientAcceptedAudioPermission())
    } else {
      yield put(videoSessionActions.patientDeclinedAudioPermission())
      logger.info('Patient declined audio permission, dismissing video call screen', {
        microphonePermission,
      })
      yield call(dismissVideoCallScreen, { type: action.type })
      showAppSettingsAlert()
      return
    }
  }

  // Bluetooth Connect permission is only available on Android 12+, and this is asked once
  if (shouldAskForBluetooth && bluetoothConnectPermission === 'denied') {
    bluetoothConnectPermission = yield* call(requestBluetoothConnectPermission)

    if (bluetoothConnectPermission === 'denied') {
      yield call(showBluetoothAlert)
    }
  }

  // Configure OpenTok audio for video call with Bluetooth support
  yield call(configureOpenTokAudioForVideoCall)

  const shouldUseNativeCall = yield* call(canUseNativeCall)
  if (shouldUseNativeCall) {
    yield put(nativeCallIOSActions.callAccepted())
  }

  const caller = yield* call(getCaller)
  const videoSessionId = yield* select(selectVideoSessionId)
  // Navigate to Video Call
  // We pass the `videoSessionId` so it's tracked for DD rum
  logger.info('patientAcceptedCall saga navigating to videoCall screen', {
    videoSessionId,
  })

  yield call(waitForNavigationReady)

  yield call(NestedNavHelper.replace, 'videoCall', { caller, videoSessionId: videoSessionId })

  if (isAndroid()) {
    yield put(nativeCallAndroidActions.callAccepted({ sessionId: onDemandSessionId }))
  }
}

export function* patientDeclinedCall(action: PayloadAction<PatientDeclinedCallPayload>) {
  const { onDemandSessionId } = action.payload

  logger.info('patientDeclinedCall saga started', {
    onDemandSessionId,
  })

  yield* call(waitForAppHydrated)

  if (onDemandSessionId) {
    yield spawn(declineCallRequest, onDemandSessionId)
  } else {
    logger.warn('patientDeclinedCall saga executing without a session ID')
  }

  yield put(
    videoSessionActions.patientEndedCall({
      declined: true,
      onDemandSessionId,
    })
  )
}

export function* waitForCallEnd() {
  yield* take(VIDEO_CALL_END_ACTIONS)
}
